import * as React from 'react';

/**
 * Whether the window has been scrolled away from the very top.
 *
 * What it is for: a header that floats transparently over a full-bleed hero
 * has to become a solid bar the moment anything else is behind it, or it turns
 * into off-white text on off-white content.
 *
 * The threshold is small on purpose. Tying the switch to the height of the
 * hero would read better in theory, but it couples the header to whatever
 * section happens to be first on the page and breaks silently the day a page
 * opens with something shorter. A few pixels of scroll is the honest signal:
 * the moment the page has moved at all, the nav is no longer part of the
 * picture behind it.
 */
export function useScrolled(threshold = 4) {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const read = () => setScrolled(window.scrollY > threshold);

    // Once up front: a reload part-way down the page restores the scroll
    // position before this ever runs, and the bar has to start solid.
    read();

    window.addEventListener('scroll', read, { passive: true });
    return () => window.removeEventListener('scroll', read);
  }, [threshold]);

  return scrolled;
}
